import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Dns as ServerIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  VpnKey as TokenIcon,
} from '@mui/icons-material';
import api from '../../api/axios';
import { Server } from '../../types';
import { useTranslation } from 'react-i18next';

interface ServerForm {
  name: string;
  ipAddress: string;
  hostname: string;
  rdpPort: string;
  adminUsername: string;
  adminPassword: string;
  maxSessions: string;
}

const initialForm: ServerForm = {
  name: '',
  ipAddress: '',
  hostname: '',
  rdpPort: '3389',
  adminUsername: '',
  adminPassword: '',
  maxSessions: '10',
};

const Servers: React.FC = () => {
  const { t } = useTranslation();
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [selectedServer, setSelectedServer] = useState<Server | null>(null);
  const [editingServer, setEditingServer] = useState<Server | null>(null);
  const [form, setForm] = useState<ServerForm>(initialForm);
  const [formErrors, setFormErrors] = useState<Partial<ServerForm>>({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const response = await api.get<Server[]>('/admin/servers');
      setServers(response.data);
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('admin.servers.fetchFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const validateForm = (isEdit: boolean): boolean => {
    const errors: Partial<ServerForm> = {};
    if (!form.name.trim()) errors.name = t('admin.servers.nameRequired');
    if (!form.ipAddress.trim()) errors.ipAddress = t('admin.servers.ipRequired');
    if (!form.rdpPort.trim()) errors.rdpPort = t('admin.servers.portRequired');
    if (isNaN(Number(form.rdpPort)) || Number(form.rdpPort) < 1 || Number(form.rdpPort) > 65535) {
      errors.rdpPort = t('admin.servers.portInvalid');
    }
    if (!form.adminUsername.trim()) errors.adminUsername = t('admin.servers.usernameRequired');
    if (!isEdit && !form.adminPassword.trim()) errors.adminPassword = t('admin.servers.passwordRequired');

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openCreateDialog = () => {
    setEditingServer(null);
    setForm(initialForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (server: Server) => {
    setEditingServer(server);
    setForm({
      name: server.name,
      ipAddress: server.ipAddress,
      hostname: server.hostname || '',
      rdpPort: String(server.rdpPort),
      adminUsername: server.adminUsername || '',
      adminPassword: '',
      maxSessions: String(server.maxSessions || 10),
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openAgentDialog = (server: Server) => {
    setSelectedServer(server);
    setAgentDialogOpen(true);
  };

  const handleSubmit = async () => {
    const isEdit = !!editingServer;
    if (!validateForm(isEdit)) return;

    setSubmitting(true);
    try {
      const payload: any = {
        name: form.name,
        ipAddress: form.ipAddress,
        hostname: form.hostname || null,
        rdpPort: parseInt(form.rdpPort),
        adminUsername: form.adminUsername,
        maxSessions: parseInt(form.maxSessions),
      };

      if (form.adminPassword.trim()) {
        payload.adminPassword = form.adminPassword;
      }

      if (isEdit) {
        await api.put(`/admin/servers/${editingServer!.id}`, payload);
        showSnackbar(t('admin.servers.updatedSuccess'), 'success');
      } else {
        await api.post('/admin/servers', payload);
        showSnackbar(t('admin.servers.createdSuccess'), 'success');
      }

      setDialogOpen(false);
      setForm(initialForm);
      setEditingServer(null);
      setFormErrors({});
      fetchServers();
    } catch (err: any) {
      const msg = isEdit ? t('admin.servers.updateFailed') : t('admin.servers.createFailed');
      showSnackbar(err.response?.data?.message || msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('admin.servers.deleteConfirm'))) return;

    try {
      await api.delete(`/admin/servers/${id}`);
      showSnackbar(t('admin.servers.deletedSuccess'), 'success');
      fetchServers();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || t('admin.servers.deleteFailed'), 'error');
    }
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    showSnackbar(t('admin.servers.tokenCopied'), 'info');
  };

  const handleRegenerateToken = async (serverId: number) => {
    if (!window.confirm(t('admin.servers.regenerateConfirm'))) return;

    try {
      const response = await api.post(`/admin/servers/${serverId}/regenerate-token`);
      showSnackbar(t('admin.servers.tokenRegenerated'), 'success');
      fetchServers();
      if (selectedServer) {
        setSelectedServer({ ...selectedServer, agentToken: response.data.agentToken });
      }
    } catch (err: any) {
      showSnackbar(t('admin.servers.fetchFailed'), 'error');
    }
  };

  const generatePowerShellScript = (server: Server): string => {
    const { protocol, hostname, port } = window.location;
    const backendUrl = port === '3000'
      ? `${protocol}//${hostname}:8080`
      : `${protocol}//${hostname}${port ? ':' + port : ''}`;
    return `# DenTech Agent - ${server.name}
# Heartbeat every 30 seconds + auto command execution

$AgentToken = "${server.agentToken}"
$HeartbeatURL = "${backendUrl}/api/agent/heartbeat"
$CommandResultURL = "${backendUrl}/api/agent/command-result"
$LastIP = ""

function Execute-Command($cmd) {
    $type = $cmd.type
    $payload = $cmd.payload | ConvertFrom-Json
    $result = ""
    $success = $true

    try {
        switch ($type) {
            "CREATE_USER" {
                $secPass = ConvertTo-SecureString $payload.windowsPassword -AsPlainText -Force
                New-LocalUser -Name $payload.windowsUsername -Password $secPass -FullName $payload.windowsUsername -Description "DenTech RDP User" -ErrorAction Stop
                Add-LocalGroupMember -Group "Remote Desktop Users" -Member $payload.windowsUsername -ErrorAction Stop
                Add-LocalGroupMember -Group "Users" -Member $payload.windowsUsername -ErrorAction SilentlyContinue
                $result = "User created: $($payload.windowsUsername)"
            }
            "DELETE_USER" {
                $sessions = query user 2>$null | Where-Object { $_ -match $payload.windowsUsername }
                if ($sessions) { $sessions | ForEach-Object { logoff ($_ -split '\\s+')[3] 2>$null } }
                Remove-LocalUser -Name $payload.windowsUsername -ErrorAction Stop
                $result = "User deleted: $($payload.windowsUsername)"
            }
            "ENABLE_USER" {
                Enable-LocalUser -Name $payload.windowsUsername -ErrorAction Stop
                $result = "User enabled: $($payload.windowsUsername)"
            }
            "DISABLE_USER" {
                Disable-LocalUser -Name $payload.windowsUsername -ErrorAction Stop
                $sessions = query user 2>$null | Where-Object { $_ -match $payload.windowsUsername }
                if ($sessions) { $sessions | ForEach-Object { logoff ($_ -split '\\s+')[3] 2>$null } }
                $result = "User disabled: $($payload.windowsUsername)"
            }
            default { $result = "Unknown command: $type"; $success = $false }
        }
    } catch {
        $result = $_.Exception.Message
        $success = $false
    }

    # Report result
    $body = @{ agentToken=$AgentToken; commandId=$cmd.id; status=$(if($success){"COMPLETED"}else{"FAILED"}); result=$result } | ConvertTo-Json
    try { Invoke-RestMethod -Uri $CommandResultURL -Method POST -Body $body -ContentType "application/json" -TimeoutSec 10 } catch {}
    Write-Host "[$([DateTime]::Now.ToString('HH:mm:ss'))] CMD $type -> $(if($success){'OK'}else{'FAIL'}): $result" -ForegroundColor $(if($success){'Green'}else{'Red'})
}

while ($true) {
    try {
        $IP = (Invoke-RestMethod -Uri "https://api.ipify.org" -TimeoutSec 10)
        $Body = @{ agentToken=$AgentToken; ipAddress=$IP; computerName=$env:COMPUTERNAME } | ConvertTo-Json
        $Res = Invoke-RestMethod -Uri $HeartbeatURL -Method POST -Body $Body -ContentType "application/json" -TimeoutSec 10
        if ($IP -ne $LastIP) {
            Write-Host "[$([DateTime]::Now.ToString('HH:mm:ss'))] IP: $LastIP -> $IP" -ForegroundColor Yellow
            $LastIP = $IP
        }
        # Execute any pending commands
        if ($Res.commands) {
            foreach ($cmd in $Res.commands) { Execute-Command $cmd }
        }
    } catch {}
    Start-Sleep -Seconds 30
}
`;
  };

  const handleDownloadScript = (server: Server) => {
    const script = generatePowerShellScript(server);
    const blob = new Blob([script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dentech-agent-${server.name.replace(/\s+/g, '-').toLowerCase()}.ps1`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const isServerOnline = (lastHeartbeat: string | null): boolean => {
    if (!lastHeartbeat) return false;
    const diff = Date.now() - new Date(lastHeartbeat).getTime();
    return diff < 10 * 60 * 1000; // 10 minutes
  };

  const handleInputChange = (field: keyof ServerForm) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setForm({ ...form, [field]: e.target.value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: undefined });
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'success';
      case 'DOWN':
        return 'error';
      case 'MAINTENANCE':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>
            {t('admin.servers.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#78909c' }}>
            {t('admin.servers.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={fetchServers} sx={{ color: '#1a237e' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3,
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              boxShadow: '0 4px 15px rgba(26,35,126,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #283593 0%, #1565c0 100%)' },
            }}
          >
            {t('admin.servers.addServer')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: '#f8f9fa',
                '& th': { fontWeight: 700, color: '#37474f', fontSize: '0.85rem', borderBottom: '2px solid #e0e0e0' },
              }}
            >
              <TableCell>{t('common.name')}</TableCell>
              <TableCell>{t('admin.servers.ipAddress')}</TableCell>
              <TableCell>{t('admin.servers.rdpPort')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell>{t('admin.servers.lastHeartbeat')}</TableCell>
              <TableCell>{t('admin.servers.maxSessions')}</TableCell>
              <TableCell align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {servers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <ServerIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#9e9e9e' }}>
                    {t('admin.servers.noServers')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              servers.map((server) => (
                <TableRow
                  key={server.id}
                  sx={{ '&:hover': { backgroundColor: '#f5f7fa' }, transition: 'background-color 0.2s' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ServerIcon sx={{ fontSize: 20, color: '#1a237e' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {server.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {server.hostname || server.ipAddress}
                    </Typography>
                    {server.hostname && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        IP: {server.ipAddress}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {server.rdpPort}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column' }}>
                      <Chip
                        label={server.status}
                        color={getStatusColor(server.status)}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.75rem', width: 'fit-content' }}
                      />
                      {server.agentToken && (
                        <Chip
                          label={isServerOnline(server.lastHeartbeat) ? t('admin.servers.online') : t('admin.servers.offline')}
                          color={isServerOnline(server.lastHeartbeat) ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20, width: 'fit-content' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#78909c', fontSize: '0.8rem' }}>
                      {server.lastHeartbeat
                        ? new Date(server.lastHeartbeat).toLocaleString()
                        : t('admin.servers.noHeartbeat')}
                    </Typography>
                  </TableCell>
                  <TableCell>{server.maxSessions}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('admin.servers.agentToken')}>
                      <IconButton
                        onClick={() => openAgentDialog(server)}
                        size="small"
                        sx={{ color: '#ff9800', '&:hover': { backgroundColor: '#fff3e0' } }}
                      >
                        <TokenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.servers.editServer')}>
                      <IconButton
                        onClick={() => openEditDialog(server)}
                        size="small"
                        sx={{ color: '#1a237e', '&:hover': { backgroundColor: '#e8eaf6' } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('admin.servers.deleteServer')}>
                      <IconButton
                        onClick={() => handleDelete(server.id)}
                        size="small"
                        sx={{ color: '#ef5350', '&:hover': { backgroundColor: '#ffebee' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Server Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setForm(initialForm); setEditingServer(null); setFormErrors({}); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{ background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)', color: '#ffffff', fontWeight: 700 }}
        >
          {editingServer ? t('admin.servers.editServerTitle') : t('admin.servers.addNewServer')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <TextField fullWidth label={t('admin.servers.serverName')} value={form.name}
            onChange={handleInputChange('name')} error={!!formErrors.name} helperText={formErrors.name} sx={{ mb: 2 }} />
          <TextField fullWidth label={t('admin.servers.ipAddress')} value={form.ipAddress}
            onChange={handleInputChange('ipAddress')} error={!!formErrors.ipAddress} helperText={formErrors.ipAddress}
            placeholder="192.168.1.100" sx={{ mb: 2 }} />
          <TextField fullWidth label={t('admin.servers.hostname')} value={form.hostname}
            onChange={handleInputChange('hostname')} placeholder="server-hostname"
            helperText={t('admin.servers.hostnameHint')} sx={{ mb: 2 }} />
          <TextField fullWidth label={t('admin.servers.rdpPort')} type="number" value={form.rdpPort}
            onChange={handleInputChange('rdpPort')} error={!!formErrors.rdpPort} helperText={formErrors.rdpPort} sx={{ mb: 2 }} />
          <TextField fullWidth label={t('admin.servers.maxSessions')} type="number" value={form.maxSessions}
            onChange={handleInputChange('maxSessions')} sx={{ mb: 2 }} />
          <TextField fullWidth label={t('admin.servers.adminUsername')} value={form.adminUsername}
            onChange={handleInputChange('adminUsername')} error={!!formErrors.adminUsername} helperText={formErrors.adminUsername} sx={{ mb: 2 }} />
          <TextField fullWidth label={t('admin.servers.adminPassword')} type="password" value={form.adminPassword}
            onChange={handleInputChange('adminPassword')} error={!!formErrors.adminPassword}
            helperText={formErrors.adminPassword || (editingServer ? t('admin.servers.passwordHint') : '')} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => { setDialogOpen(false); setForm(initialForm); setEditingServer(null); setFormErrors({}); }}
            sx={{ textTransform: 'none', fontWeight: 500 }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3,
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #283593 0%, #1565c0 100%)' },
            }}
          >
            {submitting ? <CircularProgress size={24} sx={{ color: '#fff' }} />
              : editingServer ? t('common.save') : t('admin.servers.createServer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agent Token Dialog */}
      <Dialog
        open={agentDialogOpen}
        onClose={() => setAgentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{ background: 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)', color: '#ffffff', fontWeight: 700 }}
        >
          {t('admin.servers.agentToken')} - {selectedServer?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 1 }}>
          {selectedServer && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#37474f' }}>
                {t('admin.servers.agentToken')}:
              </Typography>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, mb: 3,
                p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #e0e0e0',
              }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all', fontSize: '0.85rem' }}>
                  {selectedServer.agentToken || 'N/A'}
                </Typography>
                {selectedServer.agentToken && (
                  <Tooltip title={t('admin.servers.copyToken')}>
                    <IconButton size="small" onClick={() => handleCopyToken(selectedServer.agentToken)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#37474f' }}>
                {t('admin.servers.lastHeartbeat')}:
              </Typography>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={isServerOnline(selectedServer.lastHeartbeat) ? t('admin.servers.online') : t('admin.servers.offline')}
                  color={isServerOnline(selectedServer.lastHeartbeat) ? 'success' : 'default'}
                  size="small"
                />
                <Typography variant="body2" sx={{ color: '#78909c' }}>
                  {selectedServer.lastHeartbeat
                    ? new Date(selectedServer.lastHeartbeat).toLocaleString()
                    : t('admin.servers.noHeartbeat')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadScript(selectedServer)}
                  sx={{
                    textTransform: 'none', fontWeight: 600, borderRadius: 2,
                    background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)' },
                  }}
                >
                  {t('admin.servers.downloadScript')}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<TokenIcon />}
                  onClick={() => handleRegenerateToken(selectedServer.id)}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  {t('admin.servers.regenerateToken')}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAgentDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 500 }}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Servers;
